import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React, { useEffect, useState } from 'react';
import { MapContainer, MapContainerProps, Marker, Rectangle, TileLayer, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import { Spinner } from 'reactstrap';
import { computeGridCells, LatLng, LatLngBounds } from './utils';

/* eslint-disable @typescript-eslint/no-var-requires */
const markerIcon = require('leaflet/dist/images/marker-icon.png');
const markerShadow = require('leaflet/dist/images/marker-shadow.png');
/* eslint-enable @typescript-eslint/no-var-requires */

const DefaultIcon = L.icon({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const DraggableIcon = L.icon({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  className: 'grid-drag-marker',
});

L.Marker.prototype.options.icon = DefaultIcon;

interface CustomMapContainerProps extends MapContainerProps {
  center: [number, number];
  zoom: number;
  style: React.CSSProperties;
}

const WORLD_BOUNDS: [[number, number], [number, number]] = [
  [-90, -180],
  [90, 180],
];

export interface GridMapGrid {
  id: number;
  bounds: LatLngBounds;
}

interface GridMapProps {
  center: LatLng;
  interactive?: boolean;
  onOriginPick?: (lat: number, lng: number) => void;
  grids?: GridMapGrid[];
  highlightedGridId?: number | null;
  previewOrigin?: LatLng | null;
  previewCellSize?: number;
  previewDimension?: number;
}

export const GridMap = ({
  center,
  interactive = false,
  onOriginPick,
  grids = [],
  highlightedGridId = null,
  previewOrigin = null,
  previewCellSize,
  previewDimension,
}: GridMapProps) => {
  const zoom = 8;
  const canDragPreview = interactive && !!onOriginPick;
  const previewCells = previewOrigin ? computeGridCells(previewOrigin[0], previewOrigin[1], previewCellSize, previewDimension) : [];
  const [tilesLoaded, setTilesLoaded] = useState(false);

  return (
    <div className="h-100 w-100 position-relative" style={{ minHeight: '400px' }}>
      {!tilesLoaded && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ position: 'absolute', inset: 0, zIndex: 1000, background: 'rgba(255, 255, 255, 0.6)' }}
        >
          <Spinner color="primary" />
        </div>
      )}
      <MapContainer
        {...({
          center,
          zoom,
          style: { height: '100%', width: '100%', minHeight: '400px' },
          zoomControl: true,
          scrollWheelZoom: true,
          doubleClickZoom: true,
          dragging: true,
          touchZoom: true,
          boxZoom: true,
          keyboard: true,
          minZoom: 2,
          maxZoom: 19,
          maxBounds: WORLD_BOUNDS,
          maxBoundsViscosity: 1.0,
        } as CustomMapContainerProps)}
      >
        <TileLayer
          {...({
            url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
            noWrap: true,
            bounds: WORLD_BOUNDS,
            eventHandlers: { load: () => setTilesLoaded(true) },
          } as any)}
        />
        <SetViewOnChange center={center} />
        {interactive && onOriginPick && <OriginClickHandler onOriginPick={onOriginPick} />}
        {grids.map(grid =>
          grid.id === highlightedGridId ? (
            <Rectangle
              key={grid.id}
              bounds={grid.bounds}
              pathOptions={{ color: '#168884', weight: 3, fillColor: '#168884', fillOpacity: 0.3 }}
            />
          ) : (
            <Rectangle key={grid.id} bounds={grid.bounds} pathOptions={{ color: '#2b6cb0', weight: 1 }} />
          ),
        )}
        {previewCells.map((bounds, index) => (
          <Rectangle key={`preview-${index}`} bounds={bounds} pathOptions={{ color: '#168884', weight: 2, dashArray: '6 4' }} />
        ))}
        {previewOrigin && (
          <Marker
            {...({
              position: previewOrigin,
              icon: canDragPreview ? DraggableIcon : DefaultIcon,
              draggable: canDragPreview,
              eventHandlers: onOriginPick
                ? {
                    dragend(e) {
                      const position = e.target.getLatLng();
                      onOriginPick(position.lat, position.lng);
                    },
                  }
                : undefined,
            } as any)}
          >
            {canDragPreview && (
              <Tooltip {...({ direction: 'top', offset: [0, -38], permanent: true, className: 'grid-drag-tooltip' } as any)}>
                Drag to move
              </Tooltip>
            )}
          </Marker>
        )}
      </MapContainer>
    </div>
  );
};

const OriginClickHandler = ({ onOriginPick }: { onOriginPick: (lat: number, lng: number) => void }) => {
  useMapEvents({
    click(e) {
      onOriginPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

const SetViewOnChange = ({ center }: { center: [number, number] }) => {
  const map = useMap();

  useEffect(() => {
    if (center) {
      map.setView(center);
    }

    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 100);

    const resizeObserver = new ResizeObserver(() => {
      map.invalidateSize();
    });

    resizeObserver.observe(map.getContainer());

    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
    };
  }, [center, map]);

  return null;
};

export default GridMap;
